import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AnswerItem, FormShowType, QuestionTypes, QuestionValidationTypes, ReduxState } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import SLPInputNumber, { SLPNumberInputRef } from 'utilities/slp/inputNumber/inputNumber.index';
import { extractTitle } from 'scripts/string';
import { createQuestionTitle } from '../questionsTypes.script';
import { getQuestionBadge } from 'data/questionsBadge';

const SLPNumberQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const inputRef = useRef<SLPNumberInputRef>(null);
    const [title, setTitle] = useState(question.title);
    const index = props.answerList?.findIndex((i) => i.question.id === question.id);

    function getValue(a: any) {
        return Number(question.validations?.find((i) => i.title === a)?.value);
    }

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function validate(e?: boolean) {
        const value = inputRef.current?.getValue();
        if (question.is_required && !value) {
            if (e) inputRef.current?.setError(lang.numEmp);
            return false;
        }
        if (value && !inputRef.current?.getValidation()) {
            if (e) inputRef.current?.setError(lang.numErr);
            return false;
        }
        return true;
    }

    function setAnswer() {
        const value = inputRef.current?.getValue();
        const data = [...props.answerList];
        if ((value || props.answerList?.find((i) => i.id === question.id)?.value) && validate()) {
            const response: AnswerItem = {
                value: value ? value.toString() : '',
                question: props.question,
                id: question.id,
                answer: props.answer?.id,
            };
            index !== undefined && index > -1 ? (data[index] = response) : data?.push(response);
        } else if (index !== undefined && index > -1) data.splice(index, 1);
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const value = inputRef.current?.getValue();
        if ((value || props.answerList?.find((i) => i.id === question.id)?.value) && validate(e) && props.answer)
            props.submit({
                value: value?.toString(),
                question: question,
                id: new Date().getTime(),
                created_at: new Date().toISOString(),
            });
    }

    return (
        <SLPInputNumber
            min={getValue(QuestionValidationTypes.min)}
            max={getValue(QuestionValidationTypes.max)}
            maxEqual={getValue(QuestionValidationTypes.maxEqual)}
            minEqual={getValue(QuestionValidationTypes.minEqual)}
            hint={extractTitle(hint, false, false)}
            ref={inputRef}
            counter={props.counter}
            label={extractTitle(title, false, false)}
            image={question.image_file}
            color={props.form?.answer_color}
            labelColor={props.form?.question_color}
            required={question.is_required}
            onChange={setAnswer}
            onEnter={() => {
                if (props.form.show_type === FormShowType.single) props.next();
            }}
            formLang={props.form?.lang}
            dir={props.form.direction}
            defaultValue={
                index !== undefined && index > -1 && props.answerList[index].value
                    ? Number(props.answerList[index].value)
                    : undefined
            }
            endIcon={getQuestionBadge(QuestionTypes.number)}
        />
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPNumberQuestion));
